import { Fragment, useState } from 'react';
import type { ReactNode } from 'react';
import { ArrowTopRightOnSquareIcon, LinkIcon } from '@heroicons/react/24/outline';

import Button from '../../components/Button';
import Text from '../../components/Text';
import { CONSIGNES } from '../../helpers/plateforme';
import type { ClePlateforme } from '../../helpers/plateforme';
import { t } from '../../i18n/langue';

/**
 * Les briques communes aux six consignes du sas — #21.
 *
 * Elles portent ce qui doit être identique partout, et c'est délibéré : le pied
 * de page est le même sur les six parce qu'il contient l'échappatoire, et une
 * échappatoire qui varierait d'un écran à l'autre ne serait plus une garantie.
 */

/** L'adresse de l'app web, la seule sortie assumée du sas. */
export const HOTE_WEB = 'app.trigger-flow.com';

/** Celle de l'app mobile, à recopier dans Safari quand le menu ne coopère pas. */
export const HOTE_MOBILE = 'm.trigger-flow.com';

export function EnteteSas({ accroche }: { accroche: string }) {
    return (
        <header className="px-6 pt-8 text-center">
            <div className="flex justify-center">
                <img
                    src="/pwa/icon-192.png"
                    alt=""
                    aria-hidden="true"
                    className="h-16 w-16 rounded-2xl"
                />
            </div>
            <Text variante="titre" as="h1" className="mt-4">
                TriggerFlow
            </Text>
            <Text variante="petit" ton="attenue" className="mt-2 block leading-snug">
                {accroche}
            </Text>
        </header>
    );
}

/**
 * Un texte du dictionnaire qui porte du gras (`<b>…</b>`) et, au besoin, une icône
 * à sa place (`{icone}`) : la phrase reste entière, donc traduisible d'un bloc.
 */
export function TexteRiche({ texte, icone }: { texte: string; icone?: ReactNode }) {
    return (
        <>
            {texte.split(/(<b>.*?<\/b>|\{icone\})/).map((morceau, index) => {
                if (morceau === '{icone}') return <Fragment key={index}>{icone}</Fragment>;
                const gras = /^<b>(.*)<\/b>$/.exec(morceau);

                return gras ? <b key={index}>{gras[1]}</b> : morceau;
            })}
        </>
    );
}

/**
 * Les étapes numérotées.
 *
 * `<ol>` et non une suite de `<div>` : ces consignes sont lues à voix haute par
 * quelqu'un qui aide un collègue au téléphone, et « troisième étape » doit avoir
 * un sens pour un lecteur d'écran comme pour un humain.
 */
export function Etapes({ items }: { items: ReactNode[] }) {
    return (
        <ol className="mt-6 space-y-3 px-6">
            {items.map((item, index) => (
                <li key={index} className="flex gap-3">
                    <span
                        aria-hidden="true"
                        className="mt-0.5 flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-main-color-light text-xs font-bold text-main-color-dark"
                    >
                        {index + 1}
                    </span>
                    <Text variante="petit" className="leading-snug">
                        {item}
                    </Text>
                </li>
            ))}
        </ol>
    );
}

/** L'encadré d'avertissement — réservé à ce qui casse la promesse du produit. */
export function Avertissement({ children }: { children: ReactNode }) {
    return (
        <div className="mb-6 mt-6 px-6">
            <div className="rounded-xl bg-amber-50 px-3 py-2.5">
                <Text variante="petit" className="leading-snug text-amber-900">
                    {children}
                </Text>
            </div>
        </div>
    );
}

export function CopierAdresse() {
    const [copie, setCopie] = useState(false);

    const copier = async () => {
        try {
            await navigator.clipboard.writeText(`https://${HOTE_MOBILE}`);
            setCopie(true);
        } catch {
            // Presse-papier refusé (contexte non sécurisé, permission) : l'adresse
            // est écrite juste en dessous, en toutes lettres. Rien n'est perdu.
        }
    };

    return (
        <div className="mt-5">
            <Button
                libelle={copie ? t('sas.adresseCopiee') : t('sas.copierAdresse')}
                icone={LinkIcon}
                onPress={() => void copier()}
                variante="secondaire"
                pleineLargeur
            />
        </div>
    );
}

interface PiedSasProps {
    onChoisirConsigne: (cle: ClePlateforme) => void;
}

/**
 * Le bas de tous les sas. Trois choses, dans cet ordre, et l'ordre compte.
 *
 * 1. **« Déjà installée ? »** — une app installée est indétectable depuis le
 *    navigateur : aucune API ne le dit. Quelqu'un qui a déjà l'icône et rouvre le
 *    lien voit donc ce mur une seconde fois, et il faut le lui écrire.
 * 2. **L'échappatoire au reniflage.** C'est elle qui rend la détection d'UA
 *    acceptable (#14 l'avait écartée ailleurs) : la sanction d'une erreur devient
 *    une mauvaise consigne, jamais un blocage.
 * 3. **La seule sortie assumée : l'ordinateur.** Jamais un `m.` dégradé — c'est
 *    tout le sujet de #21.
 */
export function PiedSas({ onChoisirConsigne }: PiedSasProps) {
    const [ouvert, setOuvert] = useState(false);

    return (
        <footer className="mt-auto border-t border-border-color-light px-6 py-5">
            <Text variante="petit" ton="attenue" className="block leading-snug">
                {t('sas.dejaInstallee')}
            </Text>

            <div className="mt-3">
                <Button
                    libelle={t('sas.pasCeQueJeVois')}
                    onPress={() => setOuvert((v) => !v)}
                    variante="lien"
                    alignement="debut"
                    espacement="aucun"
                    ariaExpanded={ouvert}
                />
            </div>

            {ouvert && (
                <ul className="mt-2 space-y-1 rounded-xl bg-main-bg-color p-3">
                    {CONSIGNES.map((consigne) => (
                        <li key={consigne.cle}>
                            <Button
                                libelle={consigne.libelle}
                                onPress={() => onChoisirConsigne(consigne.cle)}
                                variante="discret"
                                alignement="debut"
                                pleineLargeur
                            />
                        </li>
                    ))}
                </ul>
            )}

            <a
                href={`https://${HOTE_WEB}`}
                className="mt-4 flex min-h-[44px] items-center gap-1.5 text-sm text-text-color-light"
            >
                <ArrowTopRightOnSquareIcon aria-hidden="true" className="h-4 w-4" />
                {t('sas.travaillerSurOrdinateur', { hote: HOTE_WEB })}
            </a>
        </footer>
    );
}
